import { STUDIO_TIME_ZONE } from './bookingStates';
import { isPlaceholderName } from './contact';

/**
 * Customer message copy (B6, tv-9yjy): the words of every text the SERVER sends a
 * customer through the one seam (packages/db customerMessages.sendCustomerMessageTx).
 * Pure — no clock, no I/O. Dates and times are always words in the studio's zone
 * ("Wed 20 May", "10:00am"), never an ISO date, whatever zone the host runs in.
 *
 * Wording of the 2-day reminder follows Anas (24 Sep, 75mw): a hype message with
 * the waiver link folded in while the waiver is unsigned — not a chase, and no
 * "Reply YES" (the booking is already confirmed). Zack/Jake may still tune words.
 */

export type CustomerMessageKind =
  | 'deposit-receipt'
  | 'booking-confirmed'
  | 'waiver-sent'
  | 'two-day-reminder'
  | 'topup-reminder'
  | 'deposit-link'
  | 'topup-move-link';

export const CUSTOMER_MESSAGE_KINDS: readonly CustomerMessageKind[] = [
  'deposit-receipt',
  'booking-confirmed',
  'waiver-sent',
  'two-day-reminder',
  'topup-reminder',
  'deposit-link',
  'topup-move-link',
];

/** The session a message talks about (all optional: a dateless deposit has none). */
export interface MessageSession {
  startIso: string;
  artistName?: string | null;
  studioName?: string | null;
}

export type CustomerMessageCopyInput =
  | { kind: 'deposit-receipt'; name: string | null; amountAud: number; session?: MessageSession | null }
  | { kind: 'booking-confirmed'; name: string | null; session: MessageSession }
  | { kind: 'waiver-sent'; name: string | null; waiverLink: string; session?: MessageSession | null }
  | {
      kind: 'two-day-reminder';
      name: string | null;
      session: MessageSession;
      waiverLink?: string | null;
      /** Studio days to the session: 2 (the usual), or 1 for a booking made or firmed after T-2. */
      daysOut?: 1 | 2;
    }
  | {
      kind: 'deposit-link';
      name: string | null;
      amountAud: number;
      payLink: string;
      session?: MessageSession | null;
      /** The client's first link for this slot (the Sweeper's gap-fill), not a resend. */
      firstLink?: boolean;
      /** How long the slot is held, in whole hours (first link only; omitted when the session starts sooner). */
      holdHours?: number | null;
    }
  | {
      kind: 'topup-reminder';
      name: string | null;
      amountAud: number;
      dueIso: string;
      outstandingAud: number;
      payLink: string;
    }
  | {
      /**
       * L5 (D02-23): a top-up client's date move, held until the partial is paid.
       * Names the NEW date and the time the hold lapses, in words.
       */
      kind: 'topup-move-link';
      name: string | null;
      partialAud: number;
      newSession: MessageSession;
      /** When the held new date lapses if the partial isn't paid. */
      untilIso: string;
      payLink: string;
    };

const DATE_FMT = new Intl.DateTimeFormat('en-AU', {
  timeZone: STUDIO_TIME_ZONE,
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});
const TIME_FMT = new Intl.DateTimeFormat('en-AU', {
  timeZone: STUDIO_TIME_ZONE,
  hour: 'numeric',
  minute: '2-digit',
  hourCycle: 'h12',
});

/** "Wed 20 May" in the studio's zone. */
export function studioDateWords(iso: string): string {
  const parts = DATE_FMT.formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}`;
}

/**
 * "10:00am" / "1:30pm" in the studio's zone. `compact` drops ":00" on the hour
 * ("10am", "5pm"; "1:30pm" stays), for the short texts (tv-94zi).
 */
export function studioTimeWords(iso: string, opts: { compact?: boolean } = {}): string {
  const parts = TIME_FMT.formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  const minute = get('minute');
  const clock = opts.compact && minute === '00' ? get('hour') : `${get('hour')}:${minute}`;
  return `${clock}${get('dayPeriod').toLowerCase().replace(/\./g, '')}`;
}

/** "$475" or "$475.50". */
export function audWords(amount: number): string {
  const n = Math.round(Number(amount) * 100) / 100;
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
}

/**
 * The name a message greets: the preferred name, else the first name — none when
 * the record only holds the IG-handle placeholder ("@handle", "Instagram DM").
 */
export function greetingName(c: { firstName: string; lastName?: string | null; preferredName?: string | null }): string | null {
  const preferred = (c.preferredName ?? '').trim();
  if (preferred && !preferred.startsWith('@')) return preferred;
  if (isPlaceholderName(c.firstName ?? '', c.lastName ?? '')) return null;
  return c.firstName.trim() || null;
}

/** " on Wed 20 May at 10:00am with Pat at Coburg" — only the parts we know. */
function sessionPhrase(s: MessageSession): string {
  const who = s.artistName ? ` with ${s.artistName}` : '';
  const where = s.studioName ? ` at ${s.studioName}` : '';
  return ` on ${studioDateWords(s.startIso)} at ${studioTimeWords(s.startIso)}${who}${where}`;
}

export function customerMessageBody(input: CustomerMessageCopyInput): string {
  const { name } = input;
  switch (input.kind) {
    case 'deposit-receipt': {
      const head = `Deposit of ${audWords(input.amountAud)} received${name ? `, ${name}` : ''}.`;
      return input.session
        ? `${head} You're booked${sessionPhrase(input.session)}.`
        : `${head} We'll be in touch to lock in your date.`;
    }
    case 'booking-confirmed':
      return `You're booked${name ? `, ${name}` : ''}! See you${sessionPhrase(input.session)}.`;
    case 'waiver-sent': {
      const when = input.session ? ` before your session on ${studioDateWords(input.session.startIso)}` : '';
      return `Hi${name ? ` ${name}` : ''}, please sign your medical waiver${when}: ${input.waiverLink}`;
    }
    case 'two-day-reminder': {
      const s = input.session;
      const who = s.artistName ? `${s.artistName} ` : 'Your session ';
      const where = s.studioName ? ` at ${s.studioName}` : '';
      const lead = input.daysOut === 1 ? 'See you tomorrow' : '2 days to go';
      const hype = `${lead}${name ? `, ${name}` : ''}! ${who}at ${studioTimeWords(s.startIso)} on ${studioDateWords(s.startIso)}${where}.`;
      return input.waiverLink ? `${hype} Sign your medical waiver before the day: ${input.waiverLink}` : hype;
    }
    case 'deposit-link': {
      const hold = input.session ? ` to hold${sessionPhrase(input.session)}` : '';
      if (input.firstLink) {
        const forSession = input.session ? ` for your session${sessionPhrase(input.session)}` : '';
        const held = input.holdHours ? `. We'll hold the spot for ${input.holdHours} hours` : '';
        return `Hi${name ? ` ${name}` : ''}, here's your ${audWords(input.amountAud)} deposit link${forSession}${held}: ${input.payLink}`;
      }
      return `Hi${name ? ` ${name}` : ''}, here's a fresh ${audWords(input.amountAud)} deposit link${hold}: ${input.payLink}`;
    }
    case 'topup-move-link': {
      // tv-94zi (Anas, 7 Oct): the short copy, e.g. "Hi Theo — pay $200 by Thu 14 May,
      // 5pm to move your session to Fri 26 June, 10am with Kai. Tap to pay: <link>".
      const s = input.newSession;
      const who = s.artistName ? ` with ${s.artistName}` : '';
      const at = (iso: string) => `${studioDateWords(iso)}, ${studioTimeWords(iso, { compact: true })}`;
      return (
        `Hi${name ? ` ${name}` : ''} — pay ${audWords(input.partialAud)} by ${at(input.untilIso)} ` +
        `to move your session to ${at(s.startIso)}${who}. Tap to pay: ${input.payLink}`
      );
    }
    case 'topup-reminder':
      return `Hi${name ? ` ${name}` : ''} — your ${audWords(input.amountAud)} deposit instalment is due ${studioDateWords(input.dueIso)}. ${audWords(input.outstandingAud)} remains on your plan. Tap to pay: ${input.payLink}`;
  }
}

/**
 * The deterministic conversation-row id of a server message: `msg-<kind>-<key>`,
 * so a retry, a replay or a second worker can never send it twice. The top-up
 * reminder keeps its pre-seam id `msg-topup-<planId>-<dueDay>`.
 */
export function customerMessageId(kind: CustomerMessageKind, key: string): string {
  return kind === 'topup-reminder' ? `msg-topup-${key}` : `msg-${kind}-${key}`;
}

const SERVER_MESSAGE_ID = new RegExp(
  `^msg-(?:topup|${CUSTOMER_MESSAGE_KINDS.filter((k) => k !== 'topup-reminder').join('|')})-`,
);

/**
 * True for an id the server's message seam owns (`customerMessageId`). A client
 * save may never write one: it could pre-claim a text so the server never sends
 * it. Client ids (`msg-<timestamp>`, `msg-suggested-…`, `convo-…`) never match.
 */
export function isServerMessageId(id: string): boolean {
  return SERVER_MESSAGE_ID.test(id);
}
